import React, { useEffect, useState } from "react";

import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import moment from "moment";

import theme from "../../constants/theme";
import Container from "../../components/Container";
import WelcomeMessage from "../../components/WelcomeMessage";
import Button from "../../components/Button";
import HospitalInfoPanel from "../../components/HospitalInfoPanel";
import NoData from "../../components/NoData";
import SurveyTable from "../../components/SurveyTable";
import NurseGallery from "./components/NurseGallery/NurseGallery";
import Title from "../../components/Title";
import ModalDialog from "../../components/Form/ModalDialog";
import HospitalSurveyForm from "../../components/Form/HospitalSurveyForm";
import { getHospitalSurvey, getOptionalQuestions, getReviewComment } from "../../api/hospital";
import { HospitalSurvey } from "../../types/hospital";
import Navbar from "../../components/Navigation/Navbar";
import { blankSurvey } from "../../constants/objects";
import NurseStatusBar from "./components/NurseStatusBar";
import OldDataNotification from "./components/OldDataNotification";
import { appVersion } from "../../constants/version";
import MetaDecorator from "../../components/MetaDecorator";
import { OptionalQuestion } from "../../components/Form/types";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      display: "flex",
      justifyContent: "space-between",
      [theme.breakpoints.only("xs")]: {
        flexDirection: "column",
      },
    },
    responsiblePerson: {
      margin: theme.spacing(2, 0),
      padding: theme.spacing(0, 2),
      border: `1px solid ${theme.palette.secondary.dark}`,
      background: theme.palette.grey[100],
      borderRadius: 4,
      color: theme.palette.grey[600],
      fontSize: 14,
      display: "flex",
      alignItems: "center",
      [theme.breakpoints.only("xs")]: {
        padding: theme.spacing(1.5, 2),
        marginBottom: theme.spacing(0),
      },
    },
    grow: { flexGrow: 1 },
    editIcon: {
      fontSize: theme.spacing(2.2),
      color: theme.palette.primary.main,
    },
    title: {
      color: theme.palette.primary.main,
      display: "inline-block",
      fontSize: theme.spacing(2.5),
      fontWeight: "bold",
      margin: theme.spacing(1.8),
    },
    description: {
      margin: theme.spacing(-2, 0, 5, 0),
    },
    approved: {
      color: "#00C2BA",
      border: "1px solid #00C2BA",
    },
    denied: {
      color: "#E60012",
      border: "1px solid #E60012",
    },
    review: {
      color: "#FFA340",
      border: "1px solid #FFA340",
    },
    bottom: {
      position: "static",
      bottom: "2px",
      right: "4px",
      float: "right",
      paddingTop: "80px",
      opacity: 0.5,
    },
  })
);

export default function NurseHomepage() {
  const classes = useStyles();
  const [surveyData, setSurveyData] = useState<HospitalSurvey>(blankSurvey);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [denyComment, setDenyComment] = useState<string>("");
  const [optionalQuestions, setOptionalQuestions] = useState<OptionalQuestion[]>([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const optionalQuestionList = await getOptionalQuestions();
        setOptionalQuestions(optionalQuestionList.data);

        const response = await getHospitalSurvey();
        setSurveyData(response.data);

        if (response?.data.status === "DENIED") {
          const commentResponse = await getReviewComment();
          setDenyComment(commentResponse?.data.comment);
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setSurveyData(blankSurvey);
        } else {
          console.error("Error fetching survey:", err);
        }
      }
    }

    fetchData();
  }, []);

  const toggleOpen = () => {
    setModalOpen((modalOpen) => !modalOpen);
  };

  function isOldData(surveyData: HospitalSurvey): boolean {
    const lastUpdate = new Date(surveyData.lastUpdate);
    const before6months = new Date();
    before6months.setMonth(before6months.getMonth() - 6);

    return moment(lastUpdate).isBefore(moment(before6months));
  }

  return (
    <>
      <MetaDecorator />
      <Navbar />
      <Box>
        <Container maxWidth="lg">
          {surveyData.status === "APPROVED" && isOldData(surveyData) && (
            <OldDataNotification
              surveyData={surveyData}
              handleDataChange={setSurveyData}
            />
          )}
          <WelcomeMessage />
          <HospitalInfoPanel />
          <div className={classes.container}>
            {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
              <Typography variant="body1" className={classes.responsiblePerson}>
                Zadnje ažurirano:{" "}
                {surveyData.userName + " " + surveyData.userSurname},{" "}
                {moment(surveyData.lastUpdate).format("DD.MM.YYYY. u HH:mm")}
              </Typography>
            )}
            {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
              <NurseStatusBar
                denyComment={denyComment}
                status={surveyData.status}
                icon={<CheckCircleIcon />}
              />
            )}
            <div className={classes.grow} />
            <Button
              type="button"
              startIcon={<CreateIcon />}
              onClick={toggleOpen}
            >
              {!surveyData.answers.length ? "Unesi" : "Uredi"}
            </Button>
          </div>
          {!surveyData.answers.length && (
            <NoData>
              Nema podataka.
              <br />
              Kliknite na gumb "Unesi" kako biste unijeli podatke.
            </NoData>
          )}
          {!!surveyData.answers.length && !isSurveyBlank(surveyData) && (
            <SurveyTable surveyData={surveyData} optionalQuestions={optionalQuestions}/>
          )}
          <ModalDialog
            modalOpen={modalOpen}
            handleClose={toggleOpen}
            maxWidth="lg"
            icon={<CreateIcon className={classes.editIcon} />}
            title={
              <Typography className={classes.title}>
                {!surveyData.answers.length ? "Unesi" : "Uredi"}
              </Typography>
            }
          >
            <HospitalSurveyForm
              handleClose={toggleOpen}
              surveyData={surveyData}
              handleDataChange={setSurveyData}
              optionalQuestions={optionalQuestions}
            />
          </ModalDialog>

          <Title label="Galerija" />
          <Typography className={classes.description}>
            Ovdje možete dodati slike iz ustanove. Fotografije trebaju biti
            <strong> vodoravne</strong> i veličine <strong>manje od 3MB</strong>
            .
          </Typography>
          <NurseGallery />
        </Container>
        <Typography className={classes.bottom}>{appVersion}</Typography>
      </Box>
    </>
  );
}

function isSurveyBlank(surveyData: HospitalSurvey): boolean {
  for (const category of surveyData.answers) {
    for (const surveyAnswer of category.surveyAnswers) {
      if (surveyAnswer.answerText && surveyAnswer.answerText.length > 0) {
        return false;
      }
    }
  }

  return true;
}
